import type { BuilderDataSource } from '../schemas/builder';
import type { TextTranslatableSchema } from '../schemas/text';
import type { ServiceRequestType } from './builder-runtime';
import { getBuilderHappyHourTimes, type BuilderHappyHourPeriod } from './data-happy-hours';
import { getBuilderWeatherText, type BuilderWeatherSnapshot } from './weather';

export interface BuilderTableData {
  wifiName?: string | null;
  wifiPassword?: string | null;
  restaurantName?: string | null;
  tableName?: string | null;
  tableNumber?: string | null;
  language?: string;
  features?: {
    callWaiter?: Partial<Record<ServiceRequestType, {
      name?: string;
      translations?: Record<string, string>;
    }>>;
  };
}

export interface BuilderDataContext {
  nowMs?: number;
  status?: Partial<Record<ServiceRequestType, boolean>>;
  lastCallAt?: Partial<Record<ServiceRequestType, Date | null>>;
  happyHours?: readonly BuilderHappyHourPeriod[];
  weather?: BuilderWeatherSnapshot;
}

export function builderRequestDataType(source: BuilderDataSource): ServiceRequestType | undefined {
  switch (source.type) {
    case 'callWaiter.main.name': case 'callWaiter.main.elapsed': return 'main';
    case 'callWaiter.secondary.name': case 'callWaiter.secondary.elapsed': return 'secondary';
    case 'callWaiter.tertiary.name': case 'callWaiter.tertiary.elapsed': return 'tertiary';
    default: return undefined;
  }
}

export function isBuilderClockDataSource(source: Pick<BuilderDataSource, 'type'>): boolean {
  return [
    'dateTime.date', 'dateTime.time', 'dateTime.weekday', 'happyHour.next.start', 'happyHour.current.end',
    'callWaiter.main.elapsed', 'callWaiter.secondary.elapsed', 'callWaiter.tertiary.elapsed',
  ].includes(source.type);
}

function formatDate(value: number | null | undefined, language: string | undefined, options: Intl.DateTimeFormatOptions): string | undefined {
  if (value === null || value === undefined || !Number.isFinite(new Date(value).getTime())) return undefined;
  try {
    return new Intl.DateTimeFormat(language, options).format(value);
  } catch {
    // An unsupported locale must not make the whole builder page unavailable.
    return new Intl.DateTimeFormat(undefined, options).format(value);
  }
}

export function formatBuilderRequestElapsed(active: boolean | undefined, lastCallAt: Date | null | undefined, nowMs: number | undefined): string | undefined {
  if (!active || !(lastCallAt instanceof Date) || !Number.isFinite(lastCallAt.getTime())
    || nowMs === undefined || !Number.isFinite(nowMs)) return undefined;
  const seconds = Math.max(0, Math.floor((nowMs - lastCallAt.getTime()) / 1_000));
  const minutes = Math.floor(seconds / 60);
  const twoDigits = (value: number) => String(value).padStart(2, '0');
  return seconds >= 3_600
    ? `${Math.floor(minutes / 60)}:${twoDigits(minutes % 60)}:${twoDigits(seconds % 60)}`
    : `${twoDigits(minutes)}:${twoDigits(seconds % 60)}`;
}

/** Resolve text only. The node template renders it as interpolation, never HTML. */
export function getBuilderDataText(
  source: BuilderDataSource, table: BuilderTableData | null | undefined,
  fallback?: TextTranslatableSchema, language = 'fallback', context: BuilderDataContext = {},
): string {
  let value: string | null | undefined;
  const locale = language === 'fallback' ? table?.language ?? fallback?.fallback : language;
  switch (source.type) {
    case 'restaurant.wifi.name': value = table?.wifiName; break;
    case 'restaurant.wifi.password': value = table?.wifiPassword; break;
    case 'restaurant.name': value = table?.restaurantName; break;
    case 'table.name': value = table?.tableName; break;
    case 'table.number': value = table?.tableNumber; break;
    case 'weather.temperature': case 'weather.feelsLike': case 'weather.condition':
    case 'weather.humidity': case 'weather.wind.speed':
      value = getBuilderWeatherText(source, context.weather, locale);
      break;
    case 'callWaiter.main.name': case 'callWaiter.secondary.name': case 'callWaiter.tertiary.name': {
      const type = builderRequestDataType(source)!;
      const request = table?.features?.callWaiter?.[type];
      value = request?.translations?.[locale ?? '']
        || request?.translations?.[table?.language ?? ''] || request?.name;
      break;
    }
    case 'dateTime.date': value = formatDate(context.nowMs, locale, { dateStyle: 'medium' }); break;
    case 'dateTime.time': value = formatDate(context.nowMs, locale, { timeStyle: 'short' }); break;
    case 'dateTime.weekday': value = formatDate(context.nowMs, locale, { weekday: 'long' }); break;
    case 'happyHour.next.start': case 'happyHour.current.end': {
      const times = getBuilderHappyHourTimes(context.happyHours ?? [], context.nowMs ?? Number.NaN);
      value = formatDate(source.type === 'happyHour.next.start' ? times.nextStart : times.currentEnd,
        locale, { weekday: 'long', hour: 'numeric', minute: '2-digit' });
      break;
    }
    case 'callWaiter.main.elapsed': case 'callWaiter.secondary.elapsed': case 'callWaiter.tertiary.elapsed': {
      const type = builderRequestDataType(source)!;
      value = formatBuilderRequestElapsed(context.status?.[type], context.lastCallAt?.[type], context.nowMs);
      break;
    }
  }
  if (typeof value === 'string' && value.length > 0) return value;
  return fallback?.translations[locale ?? language] || (fallback && fallback.translations[fallback.fallback]) || '';
}
